import React from 'react';
import { usageAPI, activitySummaryAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import StatusChip from '../components/StatusChip';
import DateField from '../components/DateField';
import ProductivityBar from '../components/ProductivityBar';
import IdleBar from '../components/IdleBar';
import RelativeBar from '../components/RelativeBar';
import useScopeFilters from '../hooks/useScopeFilters';
import { formatDuration, formatDateLabel, todayString, yesterdayString, startOfWeekString, startOfMonthString, toDateString } from '../utils/format';
import { downloadBlob } from '../utils/download';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Skeleton, Alert, Stack, ButtonGroup, Button, Select, MenuItem,
    FormControl, InputLabel, Pagination, Tabs, Tab, TextField, InputAdornment,
} from '@mui/material';
import { FileDownloadRounded, SearchRounded } from '@mui/icons-material';

const ALL = '';
const TODAY = todayString();
const YESTERDAY = yesterdayString();

const DATE_MODES = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'range', label: 'Custom Range' },
];

export default function Reports() {
    const [topTab, setTopTab]     = React.useState('performance'); // 'performance' | 'usage'
    const [usageKind, setUsageKind] = React.useState('web'); // only used when topTab === 'usage'
    const [dateMode, setDateMode] = React.useState('today');
    const [fromDate, setFromDate] = React.useState(() => toDateString(new Date(Date.now() - 6 * 86400000)));
    const [toDate, setToDate]     = React.useState(TODAY);
    const [search, setSearch]     = React.useState('');
    // Unit/Team scoping follows whichever tab is active — Performance is
    // governed by reports.activity_summary, Usage by reports.web_app_usage.
    const {
        ready: scopeReady, units, teams,
        unitId, setUnitId, teamId, setTeamId, showUnitFilter, showTeamFilter,
    } = useScopeFilters(topTab === 'performance' ? 'reports.activity_summary' : 'reports.web_app_usage');
    const [page, setPage]         = React.useState(1);
    const [records, setRecords]   = React.useState([]);
    // Usage tab only: true when the API grouped results by app/domain
    // across a multi-day range instead of one row per employee per day.
    const [grouped, setGrouped]   = React.useState(false);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [loading, setLoading]   = React.useState(true);
    const [exporting, setExporting] = React.useState(false);
    const [error, setError]       = React.useState('');

    React.useEffect(() => { setPage(1); }, [topTab, usageKind, dateMode, fromDate, toDate, unitId, teamId, search]);

    // Switching tabs changes which rows `records` should represent (and which
    // scope/featureKey governs the fetch). Clear stale rows and force the
    // loading skeleton immediately so the OLD tab's data can never render
    // under the NEW tab's row layout while the scope hook re-settles (which
    // briefly flips scopeReady false/true again) — that stale-render window
    // was producing duplicate React keys (e.g. Usage rows sharing a user_id
    // rendered through the Performance row template) and showing as blank
    // rows.
    React.useEffect(() => {
        setRecords([]);
        setGrouped(false);
        setPagination({ total: 0, pages: 1 });
        setLoading(true);
    }, [topTab, usageKind]);

    const dateParams = React.useMemo(() => {
        switch (dateMode) {
            case 'today': return { date: TODAY };
            case 'yesterday': return { date: YESTERDAY };
            case 'week': return { from_date: startOfWeekString(), to_date: TODAY };
            case 'month': return { from_date: startOfMonthString(), to_date: TODAY };
            default: return { from_date: fromDate, to_date: toDate };
        }
    }, [dateMode, fromDate, toDate]);

    React.useEffect(() => {
        if (!scopeReady) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        const params = { ...dateParams, unit_id: unitId || undefined, team_id: teamId || undefined, search: search || undefined, page };
        const handle = setTimeout(() => {
            const request = topTab === 'performance' ? activitySummaryAPI.list(params) : usageAPI.list(usageKind, params);
            request
                .then((res) => {
                    if (cancelled) return;
                    if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load report');
                    setRecords(res.data.records || []);
                    setGrouped(!!res.data.grouped);
                    setPagination(res.data.pagination || { total: 0, pages: 1 });
                })
                .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load report'); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [scopeReady, topTab, usageKind, dateParams, unitId, teamId, search, page]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { ...dateParams, unit_id: unitId || undefined, team_id: teamId || undefined, search: search || undefined };
            const res = topTab === 'performance'
                ? await activitySummaryAPI.exportCsv(params)
                : await usageAPI.exportCsv(usageKind, params);
            const suffix = dateMode === 'range' ? `${fromDate}_to_${toDate}` : dateMode;
            const prefix = topTab === 'performance' ? 'performance' : (usageKind === 'web' ? 'website-usage' : 'app-usage');
            downloadBlob(res.data, `${prefix}-${suffix}.csv`);
        } catch (err) {
            setError('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const nameLabel = usageKind === 'web' ? 'Domain' : 'Application';
    const unitLabel = topTab === 'performance' ? 'employee' : (grouped ? (usageKind === 'web' ? 'site' : 'app') : 'record');

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>Reports</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">
                            {loading ? 'Loading…' : `${pagination.total} ${pagination.total === 1 ? unitLabel : unitLabel + 's'}`}
                        </Typography>
                        <ScopeBadge featureKey={topTab === 'performance' ? 'reports.activity_summary' : 'reports.web_app_usage'} />
                    </Stack>
                </Box>
                <Button
                    variant="outlined" size="small" startIcon={<FileDownloadRounded />}
                    onClick={handleExport} disabled={exporting || loading}
                >
                    {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
            </Stack>

            <Tabs value={topTab} onChange={(_, v) => setTopTab(v)} sx={{ mb: 2, minHeight: 36 }}>
                <Tab value="performance" label="Performance" sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab value="usage" label="Usage" sx={{ minHeight: 36, textTransform: 'none' }} />
            </Tabs>

            {/* Web Usage / App Usage — only meaningful on the Usage tab */}
            {topTab === 'usage' && (
                <ButtonGroup size="small" variant="outlined" sx={{ mb: 2 }}>
                    <Button variant={usageKind === 'web' ? 'contained' : 'outlined'} onClick={() => setUsageKind('web')}>Web Usage</Button>
                    <Button variant={usageKind === 'app' ? 'contained' : 'outlined'} onClick={() => setUsageKind('app')}>App Usage</Button>
                </ButtonGroup>
            )}

            {/* Main filters — apply to both tabs */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small" placeholder="Search employee…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 200 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                />
                {showUnitFilter && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                            <MenuItem value={ALL}>All Units</MenuItem>
                            {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
                {showTeamFilter && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Team</InputLabel>
                        <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                            <MenuItem value={ALL}>All Teams</MenuItem>
                            {teams.map((t) => <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
                <ButtonGroup size="small" variant="outlined">
                    {DATE_MODES.map((m) => (
                        <Button key={m.value} variant={dateMode === m.value ? 'contained' : 'outlined'} onClick={() => setDateMode(m.value)}>
                            {m.label}
                        </Button>
                    ))}
                </ButtonGroup>
                {dateMode === 'range' && (
                    <>
                        <DateField label="From" value={fromDate} onChange={setFromDate} maxDate={toDate} />
                        <DateField label="To" value={toDate} onChange={setToDate} minDate={fromDate} maxDate={TODAY} />
                    </>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            {topTab === 'performance' ? (
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell align="right">Worked</TableCell>
                                    <TableCell>Idle Time</TableCell>
                                    <TableCell>Productivity</TableCell>
                                </TableRow>
                            ) : grouped ? (
                                <TableRow>
                                    <TableCell>{nameLabel}</TableCell>
                                    <TableCell>Rating</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total Time</TableCell>
                                    <TableCell sx={{ width: '30%' }} />
                                </TableRow>
                            ) : (
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>{nameLabel}</TableCell>
                                    <TableCell>Rating</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Usage Time</TableCell>
                                    <TableCell sx={{ width: '20%' }} />
                                </TableRow>
                            )}
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No data for this range.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : topTab === 'performance' ? (
                                records.map((r, i) => (
                                    <TableRow key={`${r.user_id}-${i}`} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{r.employee_id}</Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatDuration(r.total_worked)}</TableCell>
                                        <TableCell><IdleBar idle={r.idle_time} worked={r.total_worked} /></TableCell>
                                        <TableCell>
                                            <ProductivityBar
                                                productive={r.productive} unproductive={r.unproductive}
                                                neutral={r.neutral} unrated={r.unrated}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : grouped ? (
                                (() => {
                                    const maxUsage = records.length ? records[0].usage_time : 0;
                                    return records.map((r, i) => (
                                        <TableRow key={`${r.app_name}-${i}`} sx={{ '&:last-child td': { border: 0 } }}>
                                            <TableCell>{r.app_name}</TableCell>
                                            <TableCell><StatusChip status={r.status} /></TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDuration(r.usage_time)}</TableCell>
                                            <TableCell><RelativeBar value={r.usage_time} max={maxUsage} color={usageKind === 'web' ? '#4F46E5' : '#7C3AED'} /></TableCell>
                                        </TableRow>
                                    ));
                                })()
                            ) : (
                                (() => {
                                    const maxUsage = records.length ? Math.max(...records.map(r => r.usage_time)) : 0;
                                    return records.map((r, i) => (
                                        <TableRow key={`${r.user_id}-${r.app_name}-${r.date}-${i}`} sx={{ '&:last-child td': { border: 0 } }}>
                                            <TableCell>{r.user_name}</TableCell>
                                            <TableCell>{r.app_name}</TableCell>
                                            <TableCell><StatusChip status={r.status} /></TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateLabel(r.date)}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDuration(r.usage_time)}</TableCell>
                                            <TableCell><RelativeBar value={r.usage_time} max={maxUsage} color={usageKind === 'web' ? '#4F46E5' : '#7C3AED'} /></TableCell>
                                        </TableRow>
                                    ));
                                })()
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
